import { z } from 'zod';
import {
  HORIZON_KEYS, INNOVATION_TYPES, STRATEGY_TOOLS, gapFactorProgress, strategyProgress,
  type StrategyContent, type StrategyToolKey,
} from './strategy';
import { bmcText } from './bmc';
import { HORIZON_META } from './horizontes';
import { composeTriade } from './posicionamento';

/* ─────────────────────────────────────────────────────────────────────
   DNA — a síntese do Strategy numa camada de contexto reutilizável.
   Um snapshot é imutável: cada peça do Studio aponta para o snapshot
   que a gerou, e o DNA pode evoluir sem reescrever o passado.
   ───────────────────────────────────────────────────────────────────── */

const scored = z.object({ label: z.string(), score: z.number().int().min(0).max(100) });

export const dnaContentSchema = z.object({
  purpose: z.string(),
  vision:  z.string(),
  triad:   z.object({ believe: z.string(), through: z.string(), result: z.string(), phrase: z.string().default('') }),
  values:  z.array(z.string()),
  differentiators: z.array(scored),
  priorities:      z.array(scored),
  business: z.object({ valueProp: z.string(), segments: z.string(), relationship: z.string() }),
  horizons: z.object({
    h1: z.object({ title: z.string(), description: z.string() }),
    h2: z.object({ title: z.string(), description: z.string() }),
    h3: z.object({ title: z.string(), description: z.string() }),
  }),
  actions: z.array(z.object({ text: z.string(), priority: z.string(), when: z.string() })),
  gaps: z.array(z.object({ factor: z.string(), indicator: z.string(), maturity: z.number(), target: z.number() })),
  /** os fatores externos do Macro Cenário (oportunidades e ameaças); snapshots antigos trazem o impacto */
  trends: z.array(z.object({ dimension: z.string(), trend: z.string(), quadrant: z.enum(['oportunidade', 'ameaca']).optional(), impact: z.number().optional() })),
  /** fonte → cobertura (0–1), para a bancada saber o que está parcial */
  coverage: z.record(z.string(), z.number()),
  score: z.number().int().min(0).max(100),
  alignment: z.number().int().min(0).max(100),
});
export type DnaContent = z.infer<typeof dnaContentSchema>;

/** As fontes do DNA que o Studio consome, e de que ferramenta cada uma vem. */
export const DNA_SOURCES = {
  proposito:    { label: 'Propósito',       tool: 'visao' },
  visao:        { label: 'Visão',           tool: 'visao' },
  triade:       { label: 'Tríade de Valor', tool: 'triade' },
  valores:      { label: 'Valores',         tool: 'valores' },
  diferenciais: { label: 'Diferenciais',    tool: 'diferenciais' },
  gaps:         { label: 'Mapa de Gaps',    tool: 'gaps' },
  horizontes:   { label: 'Horizontes',      tool: 'horizontes' },
  prioridades:  { label: 'Prioridades',     tool: 'inovacao' },
  macro:        { label: 'Macro Cenário',   tool: 'macro' },
  bmc:          { label: 'Business Model',  tool: 'bmc' },
  segmentos:    { label: 'Segmentos',       tool: 'bmc' },
  acoes:        { label: 'Plano 5W2H',      tool: 'w5h2' },
} as const satisfies Record<string, { label: string; tool: StrategyToolKey }>;
export type DnaSourceKey = keyof typeof DNA_SOURCES;

type Contents = { [K in StrategyToolKey]?: StrategyContent<K> };

const PRIORITY_LABEL: Record<string, string> = { alta: 'Alta', media: 'Média', baixa: 'Baixa' };

/**
 * Consolida as 10 ferramentas num DNA. Função pura: o mesmo conteúdo
 * produz sempre o mesmo DNA — é isso que torna o snapshot confiável.
 */
export function consolidateDna(c: Contents): DnaContent {
  const progress = Object.fromEntries(STRATEGY_TOOLS.map(t => {
    const content = c[t.key];
    return [t.key, content ? strategyProgress(t.key, content as never) : 0];
  })) as Record<StrategyToolKey, number>;

  const visao = c.visao, triade = c.triade, bmc = c.bmc, hz = c.horizontes;
  const ino = c.inovacao?.types ?? [];
  const typeLabel = (k: string) => INNOVATION_TYPES.find(([key]) => key === k)?.[1] ?? k;

  const gaps = (c.gaps?.factors ?? []).flatMap(f => f.indicators.map(i => ({
    factor: f.name, indicator: i.name, maturity: i.maturity, target: i.target,
  })));

  /* Score do posicionamento: média ponderada da completude, com o
     Mapa de Gaps pesando o dobro — sem diagnóstico não há posição. */
  const weights: Partial<Record<StrategyToolKey, number>> = { gaps: 2, visao: 1.5, triade: 1.5, diferenciais: 1.5 };
  let num = 0, den = 0;
  for (const t of STRATEGY_TOOLS) { const w = weights[t.key] ?? 1; num += progress[t.key] * w; den += w; }
  const completeness = num / den;
  const maturity = gaps.length ? gaps.reduce((s, g) => s + g.maturity, 0) / (gaps.length * 5) : 0;
  const score = Math.round((completeness * .7 + maturity * .3) * 100);

  /* Alinhamento ao propósito: quanto do que se faz (gaps + ações) já
     chegou na maturidade desejada. */
  const withTarget = gaps.filter(g => g.target > 0);
  const alignment = withTarget.length
    ? Math.round(withTarget.reduce((s, g) => s + Math.min(1, g.maturity / g.target), 0) / withTarget.length * 100)
    : 0;

  const coverage: Record<string, number> = {};
  for (const [key, src] of Object.entries(DNA_SOURCES)) coverage[key] = Math.round(progress[src.tool] * 100) / 100;

  return {
    purpose: visao?.purpose ?? '',
    vision:  visao?.vision ?? '',
    triad:   {
      believe: triade?.believe ?? '', through: triade?.through ?? '', result: triade?.result ?? '',
      phrase: triade ? (triade.synthesis.trim() || composeTriade(triade)) : '',
    },
    values:  (c.valores?.values ?? []).map(v => v.name).filter(Boolean),
    differentiators: [...(c.diferenciais?.items ?? [])]
      .sort((a, b) => b.strength - a.strength)
      .map(i => ({ label: i.label, score: i.strength })),
    priorities: [...ino].sort((a, b) => b.score - a.score).slice(0, 4)
      .map(t => ({ label: typeLabel(t.key), score: t.score })),
    business: {
      valueProp:    bmc?.valueProp ?? '',
      segments:     bmc ? bmcText(bmc.segments) : '',
      relationship: bmc ? bmcText(bmc.relationship) : '',
    },
    /* o título é o do método; a descrição, as iniciativas do horizonte */
    horizons: Object.fromEntries(HORIZON_KEYS.map(h => {
      const names = (hz?.[h].initiatives ?? []).map(i => i.name.trim()).filter(Boolean);
      return [h, { title: HORIZON_META[h].label, description: names.join('; ') }];
    })) as DnaContent['horizons'],
    /* o que ainda está por fazer: as concluídas ficam no Pulse */
    actions: [...(c.w5h2?.actions ?? [])].filter(a => a.what.trim() && !a.doneAt)
      .sort((a, b) => ['alta', 'media', 'baixa'].indexOf(a.priority) - ['alta', 'media', 'baixa'].indexOf(b.priority)
        || (a.when || '9999').localeCompare(b.when || '9999'))
      .map(a => ({ text: a.what, priority: PRIORITY_LABEL[a.priority] ?? a.priority, when: a.when })),
    gaps,
    trends: (['oportunidade', 'ameaca'] as const).flatMap(q => (c.macro?.factors ?? [])
      .filter(f => f.quadrant === q && f.text.trim())
      .map(f => ({ dimension: f.dimension, trend: f.text, quadrant: q }))),
    coverage,
    score,
    alignment,
  };
}

/** Os fatores do Mapa de Gaps resumidos, para a tela e para o Pulse. */
export const gapFactorSummary = (gaps: StrategyContent<'gaps'>) =>
  gaps.factors.map(f => ({ key: f.key, name: f.name, progress: gapFactorProgress(f) }));
